import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link2, Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  IMPORT_SOURCES,
  UPLOAD_LIMITS,
  recogniseImportUrl,
} from "@rec/shared";
import { i18n, useServerText } from "@/i18n";
import { useFormat } from "@/i18n/format";
import { api } from "@/lib/api";
import { Button, Card, errorMessage } from "@/components/ui";
import { toast } from "@/components/Toaster";
import { cn } from "@/lib/utils";
import { useDialogKeys } from "@/lib/useDialogKeys";

/**
 * Import a video from a link — a YouTube video or a LinkedIn post.
 *
 * The server does the fetching; this dialog only hands over the link. It then
 * stays open for the one part worth waiting for: finding out whether the link
 * actually has a video. A LinkedIn post may be text, images or a document,
 * and the person should hear that here, beside the link they pasted — not as
 * a red card discovered in the library later.
 *
 * Once the video is found the dialog closes and the download carries on in
 * the library, like an upload does. Closing early is fine too: the import
 * keeps going and its card says what happened.
 */

/** Long enough for a slow platform, short enough that nobody stares at it. */
const CHECK_TIMEOUT_MS = 45_000;

type Phase =
  | { kind: "editing" }
  | { kind: "checking"; recordingId: string; since: number }
  | { kind: "rejected"; reason: string };

/** "YouTube or LinkedIn", joined in the interface's language. */
function useSupportedSources(): string {
  const f = useFormat();
  return f.list(
    IMPORT_SOURCES.map((s) => s.label),
    "disjunction",
  );
}

export function ImportLinkDialog({
  projectId,
  onClose,
}: {
  projectId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation(["library", "common"]);
  const f = useFormat();
  const st = useServerText();
  const sources = useSupportedSources();
  const queryClient = useQueryClient();
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [phase, setPhase] = useState<Phase>({ kind: "editing" });

  const recognised = useMemo(() => recogniseImportUrl(url), [url]);

  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogKeys(dialogRef, onClose);

  const start = useMutation({
    mutationFn: () =>
      api.importFromLink({
        url: url.trim(),
        title: title.trim() || undefined,
        projectId,
      }),
    // Shown inline below; a toast as well would say it twice.
    onError: () => {},
    onSuccess: ({ recordingId }) => {
      setPhase({ kind: "checking", recordingId, since: Date.now() });
      void queryClient.invalidateQueries({ queryKey: ["recordings"] });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const checkingId = phase.kind === "checking" ? phase.recordingId : null;

  const watched = useQuery({
    queryKey: ["import-check", checkingId],
    queryFn: () => api.getRecording(checkingId!),
    enabled: checkingId !== null,
    gcTime: 0,
    // Keeps polling through a transient failure — that is not an answer about
    // the link — while the default retry rule still stops on a 404.
    refetchInterval: 1500,
  });

  useEffect(() => {
    if (phase.kind !== "checking") return;
    // Only an answer fetched for this check counts. A cached response from an
    // earlier look at the same id would otherwise be read as the verdict.
    const recording =
      watched.dataUpdatedAt >= phase.since ? watched.data : undefined;

    if (recording?.status === "failed") {
      setPhase({
        kind: "rejected",
        reason: recording.failureReason ?? i18n.t("library:importLink.rejected"),
      });
      // The reason is on screen, so the failed card would only repeat it in
      // the library. Best-effort: if this fails the card stays and says the
      // same thing, which is still honest.
      void api
        .deleteRecording(phase.recordingId)
        .catch(() => {})
        .finally(() => queryClient.invalidateQueries({ queryKey: ["recordings"] }));
      return;
    }

    const found = recording && recording.importStage !== "checking";

    if (found) {
      toast(
        i18n.t("library:importLink.found", { title: recording.title }),
        "success",
      );
      void queryClient.invalidateQueries({ queryKey: ["recordings"] });
      onClose();
      return;
    }

    if (Date.now() - phase.since > CHECK_TIMEOUT_MS) {
      toast(i18n.t("library:importLink.stillChecking"), "success");
      onClose();
    }
  }, [
    phase,
    watched.data,
    watched.dataUpdatedAt,
    // So the timeout is still checked while polls are failing.
    watched.errorUpdatedAt,
    onClose,
    queryClient,
  ]);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!recognised || start.isPending || phase.kind === "checking") return;
    setPhase({ kind: "editing" });
    start.mutate();
  }

  const busy = start.isPending || phase.kind === "checking";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-5 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <Card
        ref={dialogRef}
        className="max-h-[85vh] w-full max-w-[440px] overflow-y-auto p-5"
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-link-title"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 id="import-link-title" className="text-[15px] font-semibold text-text">
              {t("importLink.title")}
            </h2>
            <p className="mt-1 text-[13px] leading-relaxed text-text-muted">
              {t("importLink.intro", { sources })}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label={t("common:actions.close")}>
            <X size={15} />
          </Button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-text">
              {t("importLink.videoLink")}
            </span>
            <input
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                if (phase.kind === "rejected") setPhase({ kind: "editing" });
              }}
              placeholder={IMPORT_SOURCES[0].example}
              autoFocus
              disabled={busy}
              inputMode="url"
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm text-text placeholder:text-text-faint focus:border-accent focus:outline-none disabled:opacity-60"
            />
            {url.trim() ? (
              <span
                className={cn(
                  "mt-1.5 block text-[12px]",
                  recognised ? "text-success" : "text-warning",
                )}
              >
                {recognised
                  ? t("importLink.recognised", { source: recognised.source.label })
                  : t("importLink.unrecognised", { sources })}
              </span>
            ) : null}
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-text">
              {t("importLink.titleLabel")}{" "}
              <span className="font-normal text-text-faint">
                {t("importLink.titleHint")}
              </span>
            </span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              disabled={busy}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm text-text placeholder:text-text-faint focus:border-accent focus:outline-none disabled:opacity-60"
            />
          </label>

          {phase.kind === "checking" ? (
            <p
              role="status"
              className="flex items-center gap-2 rounded-lg bg-bg-subtle px-3 py-2 text-[13px] text-text-muted"
            >
              <Loader2 size={13} className="shrink-0 animate-spin" />
              {recognised
                ? t("importLink.lookingOn", { source: recognised.source.label })
                : t("importLink.lookingOnPage")}
            </p>
          ) : null}

          {phase.kind === "rejected" ? (
            <p
              role="alert"
              className="rounded-lg bg-danger-subtle px-3 py-2 text-[13px] text-danger"
            >
              {st(phase.reason)}
            </p>
          ) : null}

          {start.error ? (
            <p
              role="alert"
              className="rounded-lg bg-danger-subtle px-3 py-2 text-[13px] text-danger"
            >
              {errorMessage(start.error, t("importLink.startFailed"))}
            </p>
          ) : null}

          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              variant="primary"
              disabled={!recognised || busy}
              title={
                !url.trim()
                  ? t("importLink.pasteFirst")
                  : !recognised
                    ? t("importLink.onlySources", { sources })
                    : undefined
              }
              className="flex-1"
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />}
              {phase.kind === "checking" ? t("importLink.checking") : t("importLink.submit")}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              {phase.kind === "checking" ? t("common:actions.close") : t("common:actions.cancel")}
            </Button>
          </div>

          <p className="text-[12px] leading-relaxed text-text-faint">
            {t("importLink.legal", { size: f.bytes(UPLOAD_LIMITS.maxBytes) })}
          </p>
        </form>
      </Card>
    </div>
  );
}

/**
 * Who has the dialog open, held outside React — the same pattern as the toasts.
 *
 * The dialog must outlive the button that opened it. In an empty library that
 * button lives in the empty state, and the import it starts is the first
 * recording — so the list stops being empty, the empty state unmounts, and a
 * dialog owned by the button vanished mid-check, taking the "that post has no
 * video" message with it. Owned by `ImportLinkHost` at the app root, it stays.
 */
type Opened = { projectId: string | null } | null;
let opened: Opened = null;
const listeners = new Set<(value: Opened) => void>();

function setOpened(value: Opened) {
  opened = value;
  for (const listener of listeners) listener(value);
}

export function openImportDialog(projectId: string | null) {
  setOpened({ projectId });
}

/** Rendered once, beside the Toaster. */
export function ImportLinkHost() {
  const [current, setCurrent] = useState<Opened>(opened);

  useEffect(() => {
    listeners.add(setCurrent);
    return () => {
      listeners.delete(setCurrent);
    };
  }, []);

  return current ? (
    <ImportLinkDialog projectId={current.projectId} onClose={() => setOpened(null)} />
  ) : null;
}

/** The button that opens the dialog — sits beside Upload wherever it appears. */
export function ImportLinkButton({
  projectId,
  compact = false,
}: {
  projectId: string | null;
  compact?: boolean;
}) {
  const { t } = useTranslation("library");
  const sources = useSupportedSources();
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => openImportDialog(projectId)}
      title={t("importLink.buttonTitle", { sources })}
    >
      <Link2 size={14} />
      {compact ? t("importLink.buttonCompact") : t("importLink.button")}
    </Button>
  );
}
